import React, { useState } from 'react';
import { ChevronRightIcon, ClipboardListIcon, BookmarkIcon } from 'lucide-react';
import { AuditTable } from './AuditTable';
import { AuditDetail } from './AuditDetail';
export type AuditActionType = 'Created' | 'Updated' | 'Deleted' | 'Approved' | 'Rejected' | 'Viewed' | 'Logged In' | 'Logged Out' | 'Password Changed' | 'Permission Changed' | 'Configuration Changed' | 'License Activated' | 'License Deactivated' | 'Pass Used';
export type AuditModuleType = 'Purchase Order' | 'Invoice' | 'Requisition' | 'Contract' | 'Expense' | 'Entity' | 'Site' | 'User' | 'Group' | 'GL Code' | 'Tax Code' | 'System Setting' | 'License' | 'Delegation' | 'Approval' | 'Tag' | 'Note' | 'Vendor';
export type FieldChange = {
  field: string;
  oldValue: string | null;
  newValue: string | null;
};
export type AuditRecord = {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userType: string;
  action: AuditActionType;
  module: AuditModuleType;
  objectId: string;
  objectName: string;
  changes: FieldChange[];
  ipAddress?: string;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  relatedNotes?: string[];
  relatedAttachments?: string[];
};
export function AuditManagement({
  locale = 'en-US'
}) {
  const [selectedAudit, setSelectedAudit] = useState<AuditRecord | null>(null);
  // Sample audit data
  const [auditRecords, setAuditRecords] = useState<AuditRecord[]>([{
    id: 'audit-001',
    timestamp: '2023-10-22T14:30:00Z',
    userId: 'user-001',
    userName: 'John Smith',
    userType: 'Administrator',
    action: 'Approved',
    module: 'Purchase Order',
    objectId: 'PO-2023-001',
    objectName: 'Server Hardware',
    changes: [{
      field: 'status',
      oldValue: 'Pending Approval',
      newValue: 'Approved'
    }],
    ipAddress: '192.168.1.100',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-001',
    siteName: 'New York HQ'
  }, {
    id: 'audit-002',
    timestamp: '2023-10-21T10:15:00Z',
    userId: 'user-002',
    userName: 'Jane Doe',
    userType: 'Procurement Officer',
    action: 'Created',
    module: 'Requisition',
    objectId: 'REQ-2023-003',
    objectName: 'Marketing Materials',
    changes: [{
      field: 'name',
      oldValue: null,
      newValue: 'Marketing Materials'
    }, {
      field: 'vendor',
      oldValue: null,
      newValue: 'PrintWorks Inc.'
    }, {
      field: 'total',
      oldValue: null,
      newValue: '3200.50'
    }],
    ipAddress: '192.168.1.101',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-002',
    siteName: 'San Francisco Office',
    relatedNotes: ['Initial request from Marketing department']
  }, {
    id: 'audit-003',
    timestamp: '2023-10-20T16:45:00Z',
    userId: 'user-005',
    userName: 'Robert Brown',
    userType: 'Finance Officer',
    action: 'Updated',
    module: 'Invoice',
    objectId: 'INV-2023-002',
    objectName: 'Office Supplies',
    changes: [{
      field: 'taxCode',
      oldValue: 'EXEMPT',
      newValue: 'STD-VAT'
    }, {
      field: 'total',
      oldValue: '1250.00',
      newValue: '1350.00'
    }],
    ipAddress: '192.168.1.102',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-001',
    siteName: 'New York HQ',
    relatedNotes: ['Tax code correction per finance policy']
  }, {
    id: 'audit-004',
    timestamp: '2023-10-19T09:30:00Z',
    userId: 'user-001',
    userName: 'John Smith',
    userType: 'Administrator',
    action: 'Configuration Changed',
    module: 'GL Code',
    objectId: 'GL-001',
    objectName: 'Marketing Expenses',
    changes: [{
      field: 'description',
      oldValue: 'Marketing General',
      newValue: 'Marketing General Expenses'
    }, {
      field: 'active',
      oldValue: 'true',
      newValue: 'true'
    }],
    ipAddress: '192.168.1.100',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.'
  }, {
    id: 'audit-005',
    timestamp: '2023-10-18T11:20:00Z',
    userId: 'user-003',
    userName: 'Michael Johnson',
    userType: 'Approver',
    action: 'Rejected',
    module: 'Expense',
    objectId: 'EXP-2023-015',
    objectName: 'Conference Travel',
    changes: [{
      field: 'status',
      oldValue: 'Pending Approval',
      newValue: 'Rejected'
    }],
    ipAddress: '192.168.1.103',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-001',
    siteName: 'New York HQ',
    relatedNotes: ['Missing receipts for hotel stay']
  }, {
    id: 'audit-006',
    timestamp: '2023-10-18T08:45:00Z',
    userId: 'user-001',
    userName: 'John Smith',
    userType: 'Administrator',
    action: 'Permission Changed',
    module: 'User',
    objectId: 'user-002',
    objectName: 'Jane Doe',
    changes: [{
      field: 'roles',
      oldValue: 'Procurement Officer',
      newValue: 'Procurement Officer, Approver'
    }],
    ipAddress: '192.168.1.100',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.'
  }, {
    id: 'audit-007',
    timestamp: '2023-10-17T14:10:00Z',
    userId: 'user-004',
    userName: 'Sarah Williams',
    userType: 'Vendor User',
    action: 'Logged In',
    module: 'System Setting',
    objectId: 'session-1234',
    objectName: 'User Session',
    changes: [],
    ipAddress: '203.0.113.45'
  }, {
    id: 'audit-008',
    timestamp: '2023-10-16T15:30:00Z',
    userId: 'user-001',
    userName: 'John Smith',
    userType: 'Administrator',
    action: 'License Activated',
    module: 'License',
    objectId: 'license-concurrent-5',
    objectName: 'Concurrent User License Pack',
    changes: [{
      field: 'status',
      oldValue: 'Inactive',
      newValue: 'Active'
    }, {
      field: 'seats',
      oldValue: '0',
      newValue: '5'
    }],
    ipAddress: '192.168.1.100',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.'
  }, {
    id: 'audit-009',
    timestamp: '2023-10-15T13:25:00Z',
    userId: 'user-002',
    userName: 'Jane Doe',
    userType: 'Procurement Officer',
    action: 'Updated',
    module: 'Vendor',
    objectId: 'vendor-002',
    objectName: 'Dell Technologies',
    changes: [{
      field: 'contactEmail',
      oldValue: 'sales@dell.example.com',
      newValue: 'enterprise.sales@dell.example.com'
    }, {
      field: 'paymentTerms',
      oldValue: 'Net 30',
      newValue: 'Net 45'
    }],
    ipAddress: '192.168.1.101',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    relatedNotes: ['Updated per new vendor agreement']
  }, {
    id: 'audit-010',
    timestamp: '2023-10-14T09:15:00Z',
    userId: 'user-001',
    userName: 'John Smith',
    userType: 'Administrator',
    action: 'Configuration Changed',
    module: 'System Setting',
    objectId: 'tolerance-001',
    objectName: 'Invoice Matching Tolerance',
    changes: [{
      field: 'percentageThreshold',
      oldValue: '5',
      newValue: '3'
    }, {
      field: 'absoluteThreshold',
      oldValue: '100',
      newValue: '75'
    }],
    ipAddress: '192.168.1.100',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.'
  }]);
  const handleViewAuditDetail = (audit: AuditRecord) => {
    setSelectedAudit(audit);
  };
  const handleBackToAuditList = () => {
    setSelectedAudit(null);
  };
  // Add handler for breadcrumb navigation
  const handleBreadcrumbClick = (level: string) => {
    if (level === 'settings') {
      // In a real app, this would navigate to settings page
      // For now, we'll just go back to audit list
      setSelectedAudit(null);
    } else if (level === 'audit') {
      setSelectedAudit(null);
    }
  };
  return <div data-id="element-15341">
      <div className="mb-8" data-id="element-15342">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-15343">Audit Log</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15344">
          Review and trace all system activities and user actions
        </p>
      </div>

      {/* Content based on whether an audit record is selected */}
      {!selectedAudit ? <AuditTable auditRecords={auditRecords} onViewAudit={handleViewAuditDetail} locale={locale} data-id="element-15345" /> : <AuditDetail audit={selectedAudit} onBack={handleBackToAuditList} locale={locale} data-id="element-15346" />}
    </div>;
}